/** Herrajes visuales del Modulador (Fase 5 — Etapa 2): símbolo técnico parametrizado + panel de propiedades.
 *  Las UNIONES Fase 4 siguen siendo la fuente técnica — aquí solo se representa y complementa. */
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { resolverSimbolo, renderSimbolo } from './simbolos/biblioteca';

const SEL = 'h-9 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1 w-full';

export const sugeridoUnion = (u) => {
  if (u?.componente_a === 'muro' || u?.componente_b === 'muro') return 'herraje fijo-cancel de baño vidrio-muro';
  const pf = u?.componente_a === 'puerta' || u?.componente_b === 'puerta';
  return `${pf ? 'bisagra' : 'herraje'} vidrio-vidrio ${String(u?.angulo || '90').replace('°', '')}°`;
};

// Reglas SAGRADAS: PUERTA→MURO = bisagra muro-vidrio · PUERTA→FIJO = bisagra vidrio-vidrio · FIJO→FIJO = herraje vidrio-vidrio
export const validarRegla = (nombre, ancla, r) => {
  const n = (nombre || '').toLowerCase();
  if (ancla?.tipo === 'union' && ancla.union) {
    const muro = ancla.union.componente_a === 'muro' || ancla.union.componente_b === 'muro';
    if (muro) {
      return n.includes('bisagra')
        ? `Regla: FIJO→MURO usa HERRAJE fijo-cancel de baño vidrio-muro (no bisagra) — insertaste "${nombre}" (se permite, verifica que sea correcto)`
        : null;
    }
    const pf = ancla.union.componente_a === 'puerta' || ancla.union.componente_b === 'puerta';
    if (pf && !(n.includes('bisagra') && n.includes('vidrio'))) {
      return `Regla: PUERTA→FIJO usa BISAGRA vidrio-vidrio — insertaste "${nombre}" (se permite, verifica que sea correcto)`;
    }
    if (!pf && n.includes('bisagra')) {
      return `Regla: FIJO→FIJO usa HERRAJE vidrio-vidrio (no bisagra) — insertaste "${nombre}" (se permite, verifica que sea correcto)`;
    }
  }
  if (ancla?.tipo === 'modulo' && ancla.modulo?.id === 'puerta' && (r?.embisagrado || 'muro') === 'muro'
      && n.includes('bisagra') && n.includes('vidrio-vidrio')) {
    return `Regla: PUERTA→MURO usa BISAGRA muro-vidrio — insertaste "${nombre}" (se permite, verifica que sea correcto)`;
  }
  return null;
};

// Símbolo técnico desde la Biblioteca de Símbolos Soluarq (paramétrico, sin imágenes estáticas)
export const HerrajeSimbolo = ({ h, x, y, sel, onPointerDown, onClick }) => {
  const sid = resolverSimbolo(h.nombre);
  const pend = !h.solo_visual && !Number(h.costo);
  const col = sel ? '#0EA5E9' : pend ? '#DC2626' : '#B45309';
  const label = (h.nombre || 'Herraje').length > 24 ? `${h.nombre.slice(0, 24)}…` : (h.nombre || 'Herraje');
  return (
    <g transform={`translate(${x} ${y})`} onPointerDown={onPointerDown} onClick={onClick}
      style={{ cursor: 'grab' }} data-testid={`mod-herraje-${h.hid}`}>
      <rect x={-150} y={-175} width={300} height={355} fill="transparent" pointerEvents="all" />
      {sel && <circle cx={0} cy={0} r={130} fill="none" stroke="#0EA5E9" strokeWidth={6} strokeDasharray="18 14" />}
      <g transform={h.orientacion === 'horizontal' ? 'rotate(90)' : undefined}>{renderSimbolo(sid, col)}</g>
      {h.etiqueta && <text x={0} y={-92} textAnchor="middle" fontSize={42} fill={col} fontWeight="800">{h.etiqueta}</text>}
      {pend && <text x={0} y={-140} textAnchor="middle" fontSize={46} fill="#DC2626" fontWeight="800">Q? PENDIENTE</text>}
      <text x={0} y={155} textAnchor="middle" fontSize={40} fill={col} fontWeight="700">{sid === 'otro_herraje' ? 'OTRO HERRAJE' : label}{h.solo_visual ? '' : ` ×${Number(h.cantidad) || '?'}`}</text>
    </g>
  );
};

// Panel de propiedades (tarjeta responsive — sin tablas comprimidas en móvil)
export const HerrajePanel = ({ h, cantidad, acabados, onPatch, onCantidad, onMover, onEliminar, onVerUnion }) => (
  <div className="border border-amber-500/50 rounded-md p-2 space-y-1.5 bg-amber-500/5" data-testid="mod-herraje-panel">
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <p className="text-sm font-black text-amber-600 truncate">⚙️ Configurar herraje — {h.nombre}</p>
      {!h.solo_visual && !Number(h.costo) && <span className="text-[10px] font-black text-red-500" data-testid="mod-herraje-pendiente">⚠️ COSTO PENDIENTE (Q0)</span>}
      {!h.solo_visual && Number(h.costo) > 0 && <span className="text-[10px] font-bold text-emerald-600" data-testid="mod-herraje-costo">Q{Number(h.costo).toFixed(2)} c/u</span>}
    </div>
    <p className="text-[10px] text-[var(--text-muted)]">SKU: {h.sku || '— (sin catálogo)'} · Ancla: {h.union_id ? `unión ${h.union_id}` : h.modulo_id ? `módulo ${h.modulo_id}` : 'punto libre'}</p>
    {h.solo_visual && (
      <p className="text-[10px] text-[var(--text-muted)]">Fuente técnica: UNIÓN Fase 4 — la cantidad/costo la aporta la unión (no se duplica en el despiece). Editar cantidad aquí actualiza la unión.
        {onVerUnion && <button type="button" onClick={onVerUnion} className="ml-1 font-bold text-amber-600 underline" data-testid="mod-herraje-ver-union">Ver unión</button>}
      </p>
    )}
    {!h.solo_visual && !Number(h.costo) && <p className="text-[10px] text-red-400">Sin precio: al "Calcular receta" aparecerá en COSTO PENDIENTE para asignarlo (solo este presupuesto o Catálogo Soluarq).</p>}
    <div className="grid grid-cols-2 sm:grid-cols-6 gap-1.5 items-end">
      <div><label className="text-[10px] font-bold text-[var(--text-muted)]">Etiqueta</label>
        <Input className="h-9 text-xs" value={h.etiqueta || ''} placeholder="H1"
          onChange={(e) => onPatch({ etiqueta: e.target.value })} data-testid="mod-herraje-etiqueta" /></div>
      <div><label className="text-[10px] font-bold text-[var(--text-muted)]">Cantidad</label>
        <Input type="number" min="0" className="h-9 text-xs" value={cantidad}
          onChange={(e) => onCantidad(e.target.value)} data-testid="mod-herraje-cant" /></div>
      <div><label className="text-[10px] font-bold text-[var(--text-muted)]">Acabado</label>
        <select className={SEL} value={h.acabado || ''} onChange={(e) => onPatch({ acabado: e.target.value })} data-testid="mod-herraje-acabado">
          <option value="">—</option>{(acabados || []).map((a) => <option key={a} value={a}>{a}</option>)}
        </select></div>
      <div><label className="text-[10px] font-bold text-[var(--text-muted)]">Orientación</label>
        <select className={SEL} value={h.orientacion || 'vertical'} onChange={(e) => onPatch({ orientacion: e.target.value })} data-testid="mod-herraje-orient">
          <option value="vertical">Vertical</option><option value="horizontal">Horizontal</option>
        </select></div>
      <div><label className="text-[10px] font-bold text-[var(--text-muted)]">X (mm)</label>
        <Input type="number" className="h-9 text-xs" value={Math.round(h.x)} onChange={(e) => onMover(Number(e.target.value), h.y)} data-testid="mod-herraje-x" /></div>
      <div><label className="text-[10px] font-bold text-[var(--text-muted)]">Y (mm)</label>
        <Input type="number" className="h-9 text-xs" value={Math.round(h.y)} onChange={(e) => onMover(h.x, Number(e.target.value))} data-testid="mod-herraje-y" /></div>
    </div>
    <Input className="h-9 text-xs" placeholder="Observaciones" value={h.observaciones || ''}
      onChange={(e) => onPatch({ observaciones: e.target.value })} data-testid="mod-herraje-obs" />
    <Button type="button" variant="outline" size="sm" className="h-9 text-[11px] text-red-500 border-red-500/50 hover:bg-red-500/10 w-full sm:w-auto"
      onClick={onEliminar} data-testid="mod-herraje-eliminar"><Trash2 size={13} className="mr-1" /> Eliminar herraje</Button>
  </div>
);
